import React, { useState } from 'react'
import Drawer from '@mui/material/Drawer'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import { QcCallIcon, EmailTabIcon, SendSmsIcon, ProfileIcon, CrossCloseIcon } from '../../../components/Icons/Icons'
import { formatMoney } from '../../../utils/amount'
import { formatBusinessDate, formatBusinessDateTime } from '../../../utils/date'
import { getStatusColorKey, buildTimeline, formatReviewReason } from './helpers'
import { gatewayLabel } from '../DailyProcessing/helpers'
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { useUiKit } from '../../../components/UiKit/UiKitContext'

const CHIP_COLOR = { success: 'success', warning: 'warning', error: 'error', info: 'info', default: 'default' }

// Plain-language answer to "why did this route to X?" from the persisted routing audit
// (WS 1241875). e.g. "greenMoney · operator override".
const ROUTED_REASON_LABEL = {
  'manual-mark': 'manual mark',
  'operator-override': 'operator override',
  'account-stored': 'account default',
  'fallback': 'fallback',
}
const formatRoutedVia = (audit = {}) => {
  const gateway = audit.routedGateway || 'unknown gateway'
  const reason = ROUTED_REASON_LABEL[audit.routedReason] || audit.routedReason
  return `${gateway} · ${reason}`
}

// LawPay Part B (Task 6): plain-language explanation of a backup-gateway failover
// (e.g. "Failover: Mint → LawPay (soft_decline)"), from the persisted `row.audit`
// failoverFrom/failoverTo/failoverReason fields — so a charge that landed on a
// backup processor after the primary declined is never a silent surprise.
const formatFailoverBadge = (audit = {}) => {
  const from = gatewayLabel(audit.failoverFrom)
  const to = gatewayLabel(audit.failoverTo)
  const code = audit.failoverReason?.code ? ` (${audit.failoverReason.code})` : ''
  return `Failover: ${from} → ${to}${code}`
}

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-divider last:border-b-0">
    <span className="text-sm text-text-secondary">{label}</span>
    <span className="text-sm font-medium text-text-primary">{value}</span>
  </div>
)

const TimelineStep = ({ step, isLast }) => (
  <div className="flex gap-3" data-testid={`timeline-step-${step.label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="flex flex-col items-center">
      <span
        className={`w-2.5 h-2.5 rounded-full mt-1 ${step.state === 'done' ? 'bg-success-main' : 'bg-divider'}`}
      />
      {!isLast && <span className="w-px flex-1 bg-divider" />}
    </div>
    <div className="pb-4">
      <p className="text-sm font-medium text-text-primary">{step.label}</p>
      <p className="text-xs text-text-secondary">{step.date ? formatBusinessDate(step.date) : step.state === 'pending' ? 'Pending' : '—'}</p>
    </div>
  </div>
)

const ContactAction = ({ icon, label, onClick }) => (
  <Button size="small" variant="outlined" startIcon={icon} onClick={onClick} data-testid={`contact-${label.toLowerCase()}`}>
    {label}
  </Button>
)

const amountText = (amount) => Number(amount || 0).toFixed(2)

const ReversalConfirm = ({ action, row, maxRefund, partialRefund, refundInput, setRefundInput, onRefund, onVoid, onClose }) => {
  const isRefund = action === 'refund'
  const typedPartial = isRefund && partialRefund

  const confirm = async () => {
    if (!isRefund) {
      await onVoid(row)
      return
    }
    const amount = typedPartial ? Number(refundInput) : maxRefund
    if (!(amount > 0)) throw new Error('Enter an amount greater than zero.')
    if (amount > maxRefund) throw new Error(`Refund cannot exceed ${formatMoney(maxRefund)}.`)
    await onRefund(row, amount)
  }

  const copy = isRefund
    ? {
      title: typedPartial ? `How much do you refund to ${row.clientName}?` : `Refund ${formatMoney(maxRefund)} to ${row.clientName}?`,
      impact: ['The gateway sends this money back to the client.', "This can't be undone."],
      confirmLabel: typedPartial ? 'Refund payment' : `Refund ${formatMoney(maxRefund)}`,
      typedText: typedPartial ? undefined : amountText(maxRefund),
    }
    : {
      title: `Void the ${formatMoney(row.amount)} charge for ${row.clientName}?`,
      impact: ['The charge is cancelled before it settles.', "This can't be undone."],
      confirmLabel: 'Void charge',
      typedText: amountText(row.amount),
    }

  return (
    <ConfirmDialog
      open={action === 'refund' || action === 'void'}
      onClose={onClose}
      tone="danger"
      onConfirm={confirm}
      confirmDisabled={typedPartial && `${refundInput}`.trim() === ''}
      {...copy}
    >
      {typedPartial && (
        <div className="flex flex-col gap-1">
          <label htmlFor="kit-refund-amount" className="text-sm text-text-primary">
            {`Type the refund amount (up to ${formatMoney(maxRefund)})`}
          </label>
          <input
            id="kit-refund-amount"
            data-testid="refund-amount-input"
            type="number"
            step="0.01"
            min="0.01"
            max={maxRefund}
            value={refundInput}
            onChange={(e) => setRefundInput(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-divider bg-paper text-text-primary text-md"
          />
        </div>
      )}
    </ConfirmDialog>
  )
}

const DetailPanel = ({
  open,
  row,
  refetching,
  onClose,
  onRefetch,
  onContact,
  onRefund = () => {},
  onVoid = () => {},
  onMarkStatus = () => {},
}) => {
  const [confirmAction, setConfirmAction] = useState(null)
  const [refundAmount, setRefundAmount] = useState(0)
  const [amountError, setAmountError] = useState(null)
  const { confirm: kitConfirm } = useUiKit()
  const [kitRefundInput, setKitRefundInput] = useState('')

  if (!row) {
    return <Drawer anchor="right" open={open} onClose={onClose} />
  }

  const statusColorKey = getStatusColorKey(row.status)
  const timeline = buildTimeline(row)

  // ELIGIBILITY COMES FROM THE SERVER — the ONE shared resolver
  // (vlg-api services/payments/reversalPolicy.js#resolveReversalOptions), which already accounts
  // for the gateway's registry capability (config/gateways.js `refund`/`voidable`), whether its
  // credentials are present in THIS environment, the payment's state, whether a refund already
  // exists, and the payment-link exclusion (plan item A7). There is no enable flag and no gateway
  // list here on purpose.
  //
  // This component MUST NOT re-derive any of that from row.processor: two surfaces computing the
  // same answer WILL drift — WS 1241875, where the Daily Processing console preview lied about
  // which gateway a charge actually used. It is also what guarantees the UI never offers an action
  // the endpoint would refuse: the endpoint calls the same resolver.
  //
  // An older payload with no `reversal` field simply offers nothing.
  const reversal = row.reversal || { canRefund: false, canVoid: false, partialRefund: false, maxRefund: 0, reason: null }
  const canRefund = reversal.canRefund === true
  const canVoid = reversal.canVoid === true
  // THE REFUND CEILING IS THE SERVER'S, NOT row.amount. capturedAmount = amount - amountPaid, and a
  // payment that took a partial payment-link payment before it was charged has amountPaid > 0 — so
  // row.amount OVERSTATES what can be refunded and pre-filling it guarantees a 400 on the
  // operator's first click. The `row.amount` fallback is for an older payload only.
  const maxRefund = Number.isFinite(Number(reversal.maxRefund)) && Number(reversal.maxRefund) > 0
    ? Number(reversal.maxRefund)
    : row.amount
  // The server's own sentence, so an absent button reads as "not applicable here" rather than an
  // error (Dolly misread the old "backend rejects unsupported states" note as a failure). Only
  // shown when neither action is available; the buttons are self-explanatory when present.
  const manageHint = (canRefund || canVoid) ? null : reversal.reason

  // Real audit trail, wired from the backend `row.audit` object (createdAt/approvedAt/
  // approvedBy/processedAt). Tolerant of an older payload where `row.audit` is undefined
  // — we only surface events that actually carry a date, and fall back to an honest
  // line when none are present. Auto-mode payments have no approver, so the approval
  // event is simply omitted (approvedAt is null) rather than faked.
  const audit = row.audit || {}
  const auditEvents = []
  if (audit.createdAt) {
    auditEvents.push({ key: 'scheduled', testId: 'audit-scheduled', label: 'Scheduled', date: audit.createdAt })
  }
  if (audit.approvedAt) {
    auditEvents.push({
      key: 'approved',
      testId: 'audit-approved',
      label: audit.approvedBy ? `Approved by ${audit.approvedBy}` : 'Approved',
      date: audit.approvedAt,
    })
  }
  if (audit.processedAt) {
    auditEvents.push({ key: 'processed', testId: 'audit-processed', label: 'Processed', date: audit.processedAt })
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} data-testid="detail-panel">
      <div className="w-[440px] max-w-[92vw] h-full flex flex-col">
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-divider">
          <div>
            <p className="text-lg font-semibold text-text-primary">{row.clientName}</p>
            <p className="text-xs text-text-secondary">{row.clientCode}</p>
            <p className="text-2xl font-semibold text-text-primary mt-2">{formatMoney(row.amount)}</p>
            <div className="flex items-center gap-2 mt-2">
              <Chip
                size="small"
                label={row.statusLabel || row.status}
                color={CHIP_COLOR[statusColorKey]}
                variant={statusColorKey === 'default' ? 'outlined' : 'filled'}
              />
              <span className="text-xs text-text-secondary">
                {row.method?.type === 'Card' ? row.method?.brand || 'Card' : 'Bank'} •••• {row.method?.last4 || '----'}
              </span>
            </div>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="close detail panel">
            <CrossCloseIcon fill="var(--color-gray-500)" />
          </IconButton>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button
              size="small"
              variant="contained"
              data-testid="refetch-status-btn"
              disabled={refetching}
              onClick={() => onRefetch(row.paymentId)}
            >
              {refetching ? 'Refetching…' : 'Refetch status'}
            </Button>
          </div>

          <div className="rounded-xl border border-divider p-4 mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-3">Timeline</p>
            {timeline.map((step, idx) => (
              <TimelineStep key={step.label} step={step} isLast={idx === timeline.length - 1} />
            ))}
          </div>

          <div className="rounded-xl border border-divider p-4 mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">Details</p>
            <DetailRow label="Payment ID" value={row.paymentId} />
            {/* Gateway transaction id — legitimately absent for declines-at-submit and
                parked technical faults (they never received one), hence the dash. */}
            <DetailRow label="Transaction ID" value={row.transactionId || '—'} />
            <DetailRow label="Processor Ref" value={row.processor || '—'} />
            <DetailRow label="Auth Code" value={row.statusDetail || '—'} />
            {/* Routing audit (WS 1241875): why this payment charged where it did. Only shown
                once the decision was stamped at charge time; older / pre-charge rows omit it. */}
            {row.audit?.routedReason && (
              <DetailRow label="Routed via" value={formatRoutedVia(row.audit)} />
            )}
            {/* The refund record, from row.refund. `confirmed: false` means the refund was
                SUBMITTED and the gateway never answered — the CAS claim is held, the payment is
                parked for review, and the money may or may not have moved. Labelling that
                "Refunded" is the difference between an operator reconciling a $500 refund and
                nobody ever finding out it was lost. Only refunded rows carry this. */}
            {row.refund && (
              <div data-testid="detail-refund">
                <DetailRow
                  label={row.refund.confirmed === false
                    ? 'Refund submitted (not confirmed)'
                    : (row.refund.partial ? 'Refunded (partial)' : 'Refunded')}
                  value={`${formatMoney(row.refund.amount)}${row.refund.at ? ` · ${formatBusinessDate(row.refund.at)}` : ''}${row.refund.reference ? ` · ${row.refund.reference}` : ''}`}
                />
              </div>
            )}
            {/* LawPay Part B (Task 6): the primary gateway soft-declined and the backend
                auto-fell-back to a backup processor. Only shown when the backend actually
                stamped a failover onto this payment's audit. */}
            {row.audit?.failoverFrom && (
              <div className="pt-2">
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  data-testid="failover-indicator"
                  label={formatFailoverBadge(row.audit)}
                />
              </div>
            )}
          </div>

          {/*
            reviewReason/reviewDetail are nullable (see helpers.formatReviewReason) — this
            section is entirely omitted when both are absent, rather than rendering an
            empty/placeholder card. reviewDetail is the gateway's verbatim text; it is shown
            as-is (no reformatting/truncation) so an operator sees exactly what the gateway said.
          */}
          {(row.reviewReason || row.reviewDetail) && (
            <div className="rounded-xl border border-divider p-4 mb-4" data-testid="review-info">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">Review</p>
              {row.reviewReason && <DetailRow label="Reason" value={formatReviewReason(row.reviewReason)} />}
              {row.reviewDetail && (
                <div className="mt-2">
                  <p className="text-xs text-text-secondary mb-1">Gateway Detail</p>
                  <p className="text-sm text-text-primary whitespace-pre-wrap break-words" data-testid="review-detail-text">
                    {row.reviewDetail}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-divider p-4 mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-3">Manage Payment</p>
            {confirmAction && !kitConfirm ? (
              <div className="flex flex-col gap-2">
                {confirmAction === 'refund' ? (
                  <>
                    <p className="text-sm text-text-primary">
                      {/* A partial-capable gateway (registry `refund: 'partial'`) lets the operator
                          edit the amount; a full-only gateway states the number it will send. */}
                      {reversal.partialRefund
                        ? `Refund how much to ${row.clientName}?`
                        : `Refund ${formatMoney(maxRefund)} to ${row.clientName}?`}{' '}
                      This can't be undone.
                    </p>
                    {reversal.partialRefund && (
                      <input
                        data-testid="refund-amount-input"
                        aria-label="Refund amount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={maxRefund}
                        value={refundAmount}
                        onChange={(e) => { setRefundAmount(Number(e.target.value)); setAmountError(null) }}
                        className="w-full px-2 py-1 border border-divider rounded"
                      />
                    )}
                    {amountError && <p className="text-xs text-error-main" data-testid="refund-amount-error">{amountError}</p>}
                  </>
                ) : (
                  <p className="text-sm text-text-primary">
                    Void the in-flight charge for {row.clientName}? This can't be undone.
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    data-testid={confirmAction === 'refund' ? 'refund-confirm-btn' : 'void-confirm-btn'}
                    onClick={() => {
                      if (confirmAction === 'refund') {
                        // Blocked client-side purely to avoid a pointless round trip; the SERVER is
                        // still the authority, and `maxRefund` IS the server's own number
                        // (reversalPolicy.resolveReversalOptions -> capturedAmount), so the two can
                        // no longer disagree. Never validate against row.amount here.
                        if (!(refundAmount > 0)) { setAmountError('Enter an amount greater than zero.'); return }
                        if (refundAmount > maxRefund) { setAmountError(`Refund cannot exceed ${formatMoney(maxRefund)}.`); return }
                        setConfirmAction(null)
                        setAmountError(null)
                        onRefund(row, refundAmount)
                        return
                      }
                      setConfirmAction(null)
                      onVoid(row)
                    }}
                  >
                    Confirm {confirmAction}
                  </Button>
                  <Button size="small" variant="text" onClick={() => { setConfirmAction(null); setAmountError(null) }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {canRefund && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      data-testid="refund-btn"
                      onClick={() => { setRefundAmount(maxRefund); setAmountError(null); setKitRefundInput(''); setConfirmAction('refund') }}
                    >
                      Refund
                    </Button>
                  )}
                  {canVoid && (
                    <Button size="small" variant="outlined" color="error" data-testid="void-btn" onClick={() => setConfirmAction('void')}>
                      Void charge
                    </Button>
                  )}
                  <Button size="small" variant="outlined" data-testid="mark-paid-btn" onClick={() => onMarkStatus(row, 'cleared')}>
                    Mark paid
                  </Button>
                  <Button size="small" variant="outlined" data-testid="mark-failed-btn" onClick={() => onMarkStatus(row, 'failed')}>
                    Mark failed
                  </Button>
                </div>
                {manageHint && (
                  <p className="text-xs text-text-secondary mt-2">{manageHint}</p>
                )}
              </>
            )}
            {kitConfirm && (
              <ReversalConfirm
                action={confirmAction}
                row={row}
                maxRefund={maxRefund}
                partialRefund={reversal.partialRefund === true}
                refundInput={kitRefundInput}
                setRefundInput={setKitRefundInput}
                onRefund={onRefund}
                onVoid={onVoid}
                onClose={() => { setConfirmAction(null); setAmountError(null) }}
              />
            )}
          </div>

          <div className="rounded-xl border border-divider p-4 mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-3">Contact Client</p>
            <div className="flex flex-wrap gap-2">
              <ContactAction icon={<QcCallIcon width={16} height={16} />} label="Call" onClick={() => onContact('call', row)} />
              <ContactAction icon={<EmailTabIcon width={16} height={16} />} label="Email" onClick={() => onContact('email', row)} />
              <ContactAction icon={<SendSmsIcon />} label="Text" onClick={() => onContact('text', row)} />
              <ContactAction icon={<ProfileIcon />} label="Profile" onClick={() => onContact('profile', row)} />
            </div>
          </div>

          <div className="rounded-xl border border-divider p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">Audit Trail</p>
            {auditEvents.length > 0 ? (
              <div className="flex flex-col gap-2">
                {auditEvents.map((event) => (
                  <div
                    key={event.key}
                    data-testid={event.testId}
                    className="flex items-center justify-between py-1 border-b border-divider last:border-b-0"
                  >
                    <span className="text-sm text-text-primary">{event.label}</span>
                    <span className="text-xs text-text-secondary">{formatBusinessDateTime(event.date)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-secondary leading-relaxed" data-testid="audit-empty">
                No audit trail is available for this payment yet.
              </p>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default DetailPanel
